import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";

import Navbar from "../../components/site/Navbar";
import Footer from "../../components/site/Footer";
import { useGarment } from "../../hooks/useGarment";
import { useGarmentDesigns } from "../../hooks/useGarmentDesigns";

function formatPrice(design, garment) {
  const amount = Number(
    design?.startingPrice ??
      design?.basePrice ??
      design?.fixedPrice ??
      garment?.startingPrice ??
      garment?.basePrice ??
      0
  );

  if (!Number.isFinite(amount) || amount <= 0) {
    return "Price on request";
  }

  return `Starts from ?${amount.toLocaleString("en-IN")}`;
}

function LoadingGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {[0, 1, 2, 3, 4, 5].map((item) => (
        <div
          key={item}
          className="card-soft overflow-hidden animate-pulse"
        >
          <div className="aspect-[4/5] bg-[#FFE7D2]" />
          <div className="p-5">
            <div className="h-7 rounded bg-[#FFE7D2] w-2/3" />
            <div className="h-4 rounded bg-[#FFF0E3] mt-3 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function GarmentDesignsPage() {
  const {
    categoryId,
    sectionId,
    garmentId,
  } = useParams();

  const navigate = useNavigate();

  const {
    garment,
    garmentLoading,
    garmentError,
  } = useGarment(garmentId);

  const {
    designs,
    designsLoading,
    designsError,
  } = useGarmentDesigns(garmentId);

  const loading = garmentLoading || designsLoading;
  const error = garmentError || designsError;

  return (
    <div className="min-h-screen bg-[#FFF8F2]">
      <Navbar onBook={() => navigate("/#services")} />

      <main className="pt-28 pb-24">
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <button
            type="button"
            onClick={() =>
              navigate(
                `/category/${categoryId}/section/${sectionId}`
              )
            }
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#F46524]"
          >
            <ArrowLeft size={16} />
            Back to items
          </button>

          <div className="mt-8 max-w-3xl">
            <div className="font-label text-[#F46524] mb-4">
              Select Design
            </div>

            <h1 className="font-display text-4xl md:text-6xl tracking-tight text-[#2B2B2B]">
              {garment?.name || "Garment"} Designs
            </h1>

            <p className="text-[#5b5a56] mt-5 text-lg leading-relaxed">
              Choose a design to continue with measurements, fabric details,
              pickup preferences and booking.
            </p>
          </div>

          <div className="mt-10 max-w-[420px]">
            <button
              type="button"
              onClick={() =>
                document
                  .getElementById("available-designs")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="card-soft p-6 text-left lift w-full"
            >
              <div className="font-display text-2xl text-[#2B2B2B]">
                Browse Designs
              </div>
              <p className="text-sm text-[#5b5a56] mt-2">
                Select from available StitchB standard designs.
              </p>
            </button>
          </div>

          <div id="available-designs" className="mt-14">
            {loading ? (
              <LoadingGrid />
            ) : error ? (
              <div className="card-soft p-8 text-center">
                <h2 className="font-display text-2xl">
                  Designs could not be loaded
                </h2>

                <p className="text-[#5b5a56] mt-2">
                  Please refresh the page and try again.
                </p>
              </div>
            ) : designs.length === 0 ? (
              <div className="card-soft p-8 text-center">
                <h2 className="font-display text-2xl">
                  No designs available yet
                </h2>

                <p className="text-[#5b5a56] mt-2">
                  Designs added through the admin panel will appear here.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {designs.map((design) => {
                  const image =
                    design.mainImage ||
                    design.image ||
                    design.imageUrl ||
                    design.thumbnailUrl ||
                    "";

                  return (
                    <button
                      key={design.id}
                      type="button"
                      onClick={() =>
                        navigate(
                          `/category/${categoryId}/section/${sectionId}/item/${garmentId}/design/${design.id}`
                        )
                      }
                      className="group card-soft text-left overflow-hidden lift"
                    >
                      <div className="aspect-[4/5] overflow-hidden bg-[#FFF3E8]">
                        {image ? (
                          <img
                            src={image}
                            alt={design.name || "Garment design"}
                            loading="lazy"
                            className="w-full h-full object-contain object-center transition-opacity duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center px-4 text-center text-[#F46524] font-semibold">
                            {design.name || "StichB Design"}
                          </div>
                        )}
                      </div>

                      <div className="p-5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <h3 className="font-display text-xl">
                              {design.name || "Design"}
                            </h3>

                            {design.description ? (
                              <p className="text-sm text-[#5b5a56] mt-2 line-clamp-2">
                                {design.description}
                              </p>
                            ) : null}

                            <div className="text-sm font-semibold text-[#F46524] mt-3">
                              {formatPrice(design, garment)}
                            </div>
                          </div>

                          <span className="w-9 h-9 rounded-full bg-[#FFEDDD] text-[#F46524] flex items-center justify-center shrink-0 group-hover:bg-[#F46524] group-hover:text-white transition-colors">
                            <ArrowRight size={16} />
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

